import React from 'react';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { Scale } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BindingLegalList } from '@/components/legal/BindingLegalList';
import { GateLegalStatusNote } from '@/components/binding/GateProposedNotice';
import { BAG_TERMS, BAG_LEGAL_EFFECTIVE } from '@/components/legal/bindingGateLegal';

const LAST_UPDATED = 'October 11, 2026';
const EFFECTIVE_DATE = 'April 22, 2026';

const Section = ({ id, title, children }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="scroll-mt-24"
  >
    <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
    <div className="space-y-3 text-sm text-gray-400 leading-relaxed">{children}</div>
  </motion.section>
);

export default function TermsOfService() {
  const sections = [
    { id: 'acceptance', label: 'Acceptance of Terms' },
    { id: 'eligibility', label: 'Eligibility' },
    { id: 'platform-description', label: 'Platform Description' },
    { id: 'accounts', label: 'User Accounts' },
    { id: 'flat-fee', label: 'Flat Fee Lending Model' },
    { id: 'ai-agents', label: 'AI Agent Services' },
    { id: 'smart-contracts', label: 'Smart Contracts & Blockchain' },
    { id: 'tokens', label: 'KOJIRU Token' },
    { id: 'kyc', label: 'KYC / AML Compliance' },
    { id: 'subscriptions', label: 'Subscriptions & Payments' },
    { id: 'ip', label: 'Intellectual Property' },
    { id: 'prohibited', label: 'Prohibited Conduct' },
    { id: 'disclaimers', label: 'Disclaimers & Limitations' },
    { id: 'risks', label: 'Risk Disclosures' },
    { id: 'indemnification', label: 'Indemnification' },
    { id: 'termination', label: 'Termination' },
    { id: 'governing-law', label: 'Governing Law' },
    { id: 'binding-gate', label: 'Binding Action Gate' },
    { id: 'changes', label: 'Changes to Terms' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <div className="container mx-auto px-6 pt-28 pb-20" data-testid="terms-of-service-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Scale className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold">Terms of Service</h1>
              <p className="text-sm text-gray-500 mt-1">Last updated: {LAST_UPDATED} | Effective: {EFFECTIVE_DATE}</p>
            </div>
          </div>
          <p className="text-gray-400 text-base leading-relaxed">
            These Terms of Service ("Terms") constitute a legally binding agreement between you ("User," "you," or "your")
            and Kojiru Technologies, Inc. ("Kojiru," "we," "us," or "our") governing your access to and use of the
            Kojiru platform, website, APIs, AI agents, smart contracts, and all related services (collectively, the "Platform").
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto flex gap-10">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block w-56 shrink-0 sticky top-28 self-start">
            <nav className="space-y-1">
              {sections.map(s => (
                <a key={s.id} href={`#${s.id}`}
                  className="block text-xs text-gray-500 hover:text-white py-1.5 px-3 rounded-lg hover:bg-white/5 transition-colors">
                  {s.label}
                </a>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="flex-1 space-y-10">

            <Section id="acceptance" title="1. Acceptance of Terms">
              <p>
                By accessing or using the Platform, you agree to be bound by these Terms and our Privacy Policy.
                If you do not agree to these Terms, you must not access or use the Platform. Your continued use
                of the Platform following any modifications to these Terms constitutes acceptance of those changes.
              </p>
              <p>
                These Terms apply to all users of the Platform, including borrowers, lenders, institutional participants,
                AI agent operators, and visitors.
              </p>
            </Section>

            <Section id="eligibility" title="2. Eligibility">
              <p>You represent and warrant that:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>You are at least 18 years of age (or the age of legal majority in your jurisdiction).</li>
                <li>You have the legal capacity to enter into a binding agreement.</li>
                <li>You are not located in, or a citizen or resident of, any jurisdiction where use of the Platform is prohibited by applicable law.</li>
                <li>You are not on any U.S. government sanctions list (OFAC SDN List, EU sanctions, or equivalent).</li>
                <li>You will comply with all applicable local, state, national, and international laws and regulations.</li>
              </ul>
            </Section>

            <Section id="platform-description" title="3. Platform Description">
              <p>
                Kojiru is a technology platform that facilitates peer-to-peer lending connections between borrowers and lenders.
                The Platform provides tools including:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong className="text-white">Flat Fee Home Lending Facilitation:</strong> A proprietary mortgage structure comparison tool where interest is replaced by a one-time flat fee after year 15 of a 30-year loan.</li>
                <li><strong className="text-white">AI-Powered Analysis:</strong> Four live AI agents (Rate Optimization, Pre-Qualification, Risk Assessment, Document Processing) powered by Gemini that provide informational analysis only.</li>
                <li><strong className="text-white">Agent Credit Protocol:</strong> Infrastructure for autonomous AI agents to access credit, build reputation, and participate in the knowledge economy.</li>
                <li><strong className="text-white">On-Chain Contracts:</strong> Smart contracts deployed on Base Mainnet for token staking, governance, and protocol operations.</li>
              </ul>
              <p className="text-amber-400/80 font-medium mt-2">
                Kojiru is a facilitation platform. We are not a bank, lender, or licensed mortgage originator.
                We do not make loans, set interest rates, or guarantee loan approval. All lending decisions are
                made by licensed lenders on the platform. Kojiru provides technology tools only.
              </p>
              <p className="text-amber-400/80 font-medium mt-2">
                Kojiru is not licensed as a mortgage broker or originator. State licensing requirements may apply
                to lenders using the platform. The Flat Fee loan structure is a novel concept; its classification
                under Qualified Mortgage (QM) rules has not been determined by regulators.
              </p>
            </Section>

            <Section id="accounts" title="4. User Accounts">
              <p>To access certain features, you must create an account. You agree to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Provide accurate, current, and complete registration information.</li>
                <li>Maintain and promptly update your account information.</li>
                <li>Maintain the security of your password and accept all risk of unauthorized access to your account.</li>
                <li>Promptly notify us of any unauthorized use of your account.</li>
                <li>Not create accounts for the purpose of abusing promotions, referral programs, or platform mechanics.</li>
              </ul>
              <p>
                You are responsible for all activities that occur under your account. We reserve the right to suspend
                or terminate accounts that violate these Terms.
              </p>
            </Section>

            <Section id="flat-fee" title="5. Flat Fee Lending Model">
              <p>
                Kojiru's Flat Fee structure is a comparison tool that models an alternative to conventional 30-year
                fixed-rate mortgages. Key disclosures:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Savings calculations ($40,000+ per $100K borrowed at Grade A) are based on mathematical models comparing a conventional 30-year amortization against the Kojiru Flat Fee structure.</li>
                <li>Actual savings depend on loan amount, interest rate, credit tier, and hold period.</li>
                <li>The Flat Fee structure requires the borrower to hold the loan for at least 15 years to realize the full benefit. <strong className="text-white">Borrowers who sell or refinance before year 15 will not realize Flat Fee savings.</strong></li>
                <li>Credit tiers (Grade A, B, C) are assigned based on credit score and debt-to-income ratio and determine the flat fee percentage (10%, 12%, or 15% respectively).</li>
                <li>All calculations are provided for informational and comparison purposes only and do not constitute a loan offer, commitment, or guarantee.</li>
                <li>The Flat Fee loan structure is a novel concept. Its classification under Qualified Mortgage (QM) rules has not been determined by the CFPB or state regulators. Lenders should consult their own compliance counsel.</li>
              </ul>
            </Section>

            <Section id="ai-agents" title="6. AI Agent Services">
              <p>
                The Platform provides AI-powered analysis agents. You acknowledge and agree that:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>AI agent outputs are <strong className="text-white">informational only</strong> and do not constitute financial advice, lending decisions, or professional recommendations.</li>
                <li>AI analysis, including pre-qualification assessments, risk grades, and rate comparisons, is generated by machine learning models (Gemini) and may contain errors or inaccuracies.</li>
                <li>Document Processing (Agent 4) uses optical character recognition to extract data from uploaded documents. Extracted data should be reviewed for accuracy before use.</li>
                <li>You should consult qualified financial, legal, and tax professionals before making lending or borrowing decisions.</li>
                <li>We do not guarantee the accuracy, completeness, or reliability of any AI-generated content.</li>
                <li>Kojiru's AI models are designed to evaluate borrowers on financial merit rather than demographic factors. AI models have not been independently audited for fair lending compliance under the Equal Credit Opportunity Act (ECOA). Kojiru is committed to ongoing testing for disparate impact.</li>
              </ul>
            </Section>

            <Section id="smart-contracts" title="7. Smart Contracts & Blockchain">
              <p>
                The Platform utilizes smart contracts deployed on Base Mainnet (an Ethereum Layer 2 network).
                You acknowledge and agree that:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Smart contracts are immutable once deployed. While our contracts have been verified on Sourcify and Basescan and analyzed with static analysis tools (Slither), they have not been audited by an independent third-party firm.</li>
                <li>Blockchain transactions are irreversible. Once a transaction is confirmed on-chain, it cannot be undone.</li>
                <li>You are solely responsible for managing your private keys and wallet security.</li>
                <li>Gas fees (paid in ETH on Base) are required for on-chain transactions and are not refundable.</li>
                <li>Smart contract risks include but are not limited to: bugs, exploits, network congestion, and protocol upgrades.</li>
                <li>Fund transfers between parties are facilitated through smart contracts on the Base blockchain. Kojiru does not custody, hold, or transmit user funds directly. Users are responsible for compliance with applicable money transmission laws in their jurisdiction.</li>
              </ul>
            </Section>

            <Section id="tokens" title="8. KOJIRU Token">
              <p>
                The KOJIRU token (on-chain ticker: KUTRO) is a utility token on Base Mainnet. You acknowledge:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>The KOJIRU token is a <strong className="text-white">utility token</strong> intended for platform governance and access to protocol features. It is not a security, investment contract, or financial instrument. No representation is made regarding future token value.</li>
                <li>Consistent with the <strong className="text-white">SEC/CFTC Joint Interpretation of March 17, 2026</strong>, Kojiru classifies the KOJIRU token as a <strong className="text-white">"Digital Tool"</strong> an asset that serves a utilitarian purpose (governance, protocol access, credential) and is intended to be used or consumed rather than held for investment. Under this interpretation, digital tools are not securities.</li>
                <li>No explicit or implicit representations are made regarding future managerial efforts that would create an "investment contract" under <em>SEC v. W.J. Howey Co.</em> Staking parameters are determined by immutable smart contract code, not by discretionary management decisions.</li>
                <li>Token value may fluctuate significantly. Staking does not guarantee returns. You may lose some or all of the value of your tokens.</li>
                <li>Staking involves locking tokens for a minimum period. Early withdrawal incurs a 2% burn penalty as enforced by the smart contract. Staking parameters are set by the smart contract and may vary.</li>
                <li>Governance voting power is derived from staked tokens. Delegation of voting power is available.</li>
                <li>The token has not been registered with the SEC or any securities regulator. While the March 2026 SEC interpretation supports the classification of utility tokens as non-securities, Kojiru has not yet received an independent legal opinion confirming this classification for the KOJIRU token specifically.</li>
                <li>Stablecoins used on the Platform (USDC, USDT, DAI) are classified as payment stablecoins under the GENIUS Act of 2025 and are expressly excluded from the definition of a "security" under federal securities laws per the March 2026 SEC/CFTC interpretation.</li>
                <li>Participation in any feature that involves capital deployment (including the Credit Marketplace) may constitute a securities offering and is restricted to accredited investors as defined by SEC Rule 501.</li>
              </ul>
            </Section>

            <Section id="kyc" title="9. KYC / AML Compliance">
              <p>
                Certain Platform features require identity verification through our KYC/AML provider (iDenfy).
                By using these features, you consent to:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Providing government-issued identification and personal information for verification.</li>
                <li>Third-party identity verification processing by iDenfy in accordance with their privacy policy.</li>
                <li>Ongoing monitoring as required by applicable anti-money laundering regulations.</li>
                <li>Account restrictions or termination if verification fails or suspicious activity is detected.</li>
              </ul>
            </Section>

            <Section id="subscriptions" title="10. Subscriptions & Payments">
              <p>
                Lender features require a $55/month subscription processed through our third-party payment processor.
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Subscriptions are billed monthly and auto-renew unless canceled.</li>
                <li>You may cancel at any time; access continues through the end of the billing period.</li>
                <li>Refunds are handled under our payment processor&rsquo;s policies.</li>
                <li>We reserve the right to modify subscription pricing with 30 days notice.</li>
              </ul>
              <p className="mt-2">
                <strong className="text-white">Loan Servicing:</strong> Kojiru does not service mortgage loans. Loan servicing,
                including payment collection, escrow management, and default handling, is the responsibility of the
                originating lender or their designated servicer.
              </p>
            </Section>

            <Section id="ip" title="11. Intellectual Property">
              <p>
                All content, code, designs, algorithms, AI models, documentation, and branding on the Platform
                are owned by or licensed to Kojiru Technologies, Inc. You may not:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Copy, modify, distribute, or create derivative works from Platform content without written permission.</li>
                <li>Reverse-engineer, decompile, or disassemble any Platform technology.</li>
                <li>Use Kojiru branding, trademarks, or logos without authorization.</li>
                <li>Scrape, crawl, or automatically extract data from the Platform.</li>
              </ul>
              <p>
                The Kojiru Agent SDK is provided under the MIT License as specified in its documentation.
              </p>
            </Section>

            <Section id="prohibited" title="12. Prohibited Conduct">
              <p>You agree not to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Use the Platform for any unlawful purpose or in violation of any applicable law.</li>
                <li>Provide false, misleading, or fraudulent information (including on loan applications or KYC).</li>
                <li>Attempt to manipulate credit scores, AI outputs, or protocol mechanics.</li>
                <li>Engage in market manipulation, wash trading, or artificial inflation of platform metrics.</li>
                <li>Attempt to exploit smart contract vulnerabilities. Responsible disclosure should be directed to security@kojiru.com.</li>
                <li>Circumvent rate limits, access controls, or security measures.</li>
                <li>Use automated tools (bots, scripts) to interact with the Platform without authorization.</li>
                <li>Harass, threaten, or impersonate other users or Kojiru personnel.</li>
              </ul>
            </Section>

            <Section id="disclaimers" title="13. Disclaimers & Limitations of Liability">
              <p className="font-medium text-white">
                THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS,
                IMPLIED, OR STATUTORY.
              </p>
              <p>To the maximum extent permitted by law:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>We disclaim all warranties, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</li>
                <li>We do not warrant that the Platform will be uninterrupted, error-free, or secure.</li>
                <li>We are not responsible for any loss of funds due to smart contract exploits, wallet compromises, or blockchain network issues.</li>
                <li>Our total aggregate liability for any claim arising from your use of the Platform shall not exceed the greater of (a) $100 or (b) the fees you have paid to us in the 12 months preceding the claim.</li>
                <li>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages.</li>
              </ul>
            </Section>

            <Section id="risks" title="14. Risk Disclosures">
              <p>
                Using the Platform involves significant risks. You acknowledge and accept:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong className="text-white">Lending Risk:</strong> P2P lending carries the risk of borrower default. Lenders may lose some or all of their capital.</li>
                <li><strong className="text-white">Smart Contract Risk:</strong> Despite verification and formal analysis, smart contracts may contain undiscovered vulnerabilities.</li>
                <li><strong className="text-white">Market Risk:</strong> Cryptocurrency and token values are volatile and may decline significantly.</li>
                <li><strong className="text-white">Regulatory Risk:</strong> The regulatory environment for DeFi and crypto lending is evolving. Future regulations may restrict or prohibit certain Platform features.</li>
                <li><strong className="text-white">Technology Risk:</strong> The Platform depends on third-party infrastructure (Base network, Ethereum, IPFS) that may experience outages or failures.</li>
                <li><strong className="text-white">AI Risk:</strong> AI-generated analysis may be inaccurate. Do not rely solely on AI outputs for financial decisions.</li>
              </ul>
            </Section>

            <Section id="indemnification" title="15. Indemnification">
              <p>
                You agree to indemnify, defend, and hold harmless Kojiru Technologies, Inc., its officers, directors,
                employees, agents, and affiliates from and against any claims, liabilities, damages, losses, costs,
                and expenses (including reasonable attorneys' fees) arising from or relating to:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Your use of the Platform.</li>
                <li>Your violation of these Terms.</li>
                <li>Your violation of any applicable law or regulation.</li>
                <li>Your violation of any rights of a third party.</li>
              </ul>
            </Section>

            <Section id="termination" title="16. Termination">
              <p>
                We may suspend or terminate your access to the Platform at any time, with or without cause, with or
                without notice. Upon termination:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Your right to use the Platform ceases immediately.</li>
                <li>Outstanding on-chain positions (staking, governance, escrows) remain governed by the smart contracts and are accessible via your wallet.</li>
                <li>We may retain your data as required by law or for legitimate business purposes.</li>
              </ul>
              <p>
                You may terminate your account at any time by contacting wayne@kojiru.com.
              </p>
            </Section>

            <Section id="governing-law" title="17. Governing Law & Dispute Resolution">
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of Tennessee,
                United States, without regard to its conflict of law provisions.
              </p>
              <p>
                Any dispute arising from these Terms shall first be attempted to be resolved through good-faith
                negotiation. If unresolved within 30 days, disputes shall be submitted to binding arbitration
                in accordance with the rules of the American Arbitration Association, conducted in Nashville, Tennessee.
              </p>
              <p>
                You agree to waive any right to participate in a class action lawsuit or class-wide arbitration.
              </p>
            </Section>

            <Section id="binding-gate" title="18. Binding Action Gate">
              <GateLegalStatusNote testid="terms-bag-status-note" />
              <p>Effective {BAG_LEGAL_EFFECTIVE}. This section covers consent receipts signed before the pause, the paid gate route as it was offered, and the public receipt ledger. A standalone copy is at <Link to="/binding-gate/terms" className="text-emerald-400 hover:underline" data-testid="terms-bag-standalone-link">kojiru.com/binding-gate/terms</Link>.</p>
              <BindingLegalList items={BAG_TERMS} testid="terms-bag-list" />
            </Section>

            <Section id="changes" title="19. Changes to Terms">
              <p>
                We reserve the right to modify these Terms at any time. Material changes will be communicated via
                email or a prominent notice on the Platform at least 30 days before taking effect. Your continued
                use of the Platform after such changes constitutes acceptance of the revised Terms.
              </p>
            </Section>

            <Section id="contact" title="20. Contact">
              <p>For questions about these Terms, contact us at:</p>
              <div className="mt-3 p-4 rounded-xl bg-white/[0.03] border border-white/10">
                <p className="text-white font-medium">Kojiru Technologies, Inc.</p>
                <p className="mt-1">
                  Email: <a href="mailto:wayne@kojiru.com" className="text-emerald-400 hover:underline">wayne@kojiru.com</a>
                </p>
                <p>
                  Security: <a href="mailto:security@kojiru.com" className="text-emerald-400 hover:underline">security@kojiru.com</a>
                </p>
                <p className="mt-2 text-xs text-gray-600">
                  For responsible disclosure of security vulnerabilities, please email security@kojiru.com.
                </p>
              </div>
            </Section>

            {/* Copyright */}
            <div className="pt-8 border-t border-white/5 text-center">
              <p className="text-xs text-gray-600">&copy; 2026 Kojiru Technologies, Inc. All Rights Reserved.</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
